import type { PropsWithChildren } from 'react';
import * as React from 'react';
import { useLocation, useRouteError } from 'react-router';
// @ts-expect-error
import { type NonIndexRouteObject } from 'react-router/dom';
import { type Parser, parseRoute, type RouteDefinition, type RouteProps } from '@epihub/core/src/routes';

export type ScreenProps<
  Name extends string = string,
  Path extends string = string,
  ParsePath extends Record<string, Parser<any>> = Record<string, Parser<any>>,
  ParseHash extends Record<string, Parser<any>> = Record<string, Parser<any>>,
  ParseSearch extends Record<string, Parser<any>> = Record<string, Parser<any>>,
> = {
  loc: RouteProps<Name, Path, ParsePath, ParseHash, ParseSearch>;
};

type RenderElement<
  Name extends string = string,
  Path extends string = string,
  ParsePath extends Record<string, Parser<any>> = Record<string, Parser<any>>,
  ParseHash extends Record<string, Parser<any>> = Record<string, Parser<any>>,
  ParseSearch extends Record<string, Parser<any>> = Record<string, Parser<any>>,
> = React.ElementType<
  PropsWithChildren<
    ScreenProps<
      Name,
      Path,
      ParsePath,
      ParseHash,
      ParseSearch
    >
  >
>;

const ErrorBoundary = () => {
  // Bubble the error up to the root of the app
  throw useRouteError();
};

export function Screen<
  Name extends string = string,
  Path extends string = string,
  ParsePath extends Record<string, Parser<any>> = Record<string, Parser<any>>,
  ParseHash extends Record<string, Parser<any>> = Record<string, Parser<any>>,
  ParseSearch extends Record<string, Parser<any>> = Record<string, Parser<any>>,
>(
  route: RouteDefinition<Name, Path, ParsePath, ParseHash, ParseSearch>,
  Component: RenderElement<Name, Path, ParsePath, ParseHash, ParseSearch>,
  params?: {
    title?: string;
    children?: NonIndexRouteObject[];
  },
) {
  return function(): NonIndexRouteObject {
    return {
      path: route.path,
      caseSensitive: false,
      ErrorBoundary,
      element: (
        <ScreenWrapper<Name, Path, ParsePath, ParseHash, ParseSearch>
          route={route}
          Component={Component}
          params={params}
        />
      ),
      children: params?.children,
    };
  };
}

function ScreenWrapper<
  Name extends string = string,
  Path extends string = string,
  ParsePath extends Record<string, Parser<any>> = Record<string, Parser<any>>,
  ParseHash extends Record<string, Parser<any>> = Record<string, Parser<any>>,
  ParseSearch extends Record<string, Parser<any>> = Record<string, Parser<any>>,
>(props: {
  route: RouteDefinition<Name, Path, ParsePath, ParseHash, ParseSearch>;
  Component: RenderElement<Name, Path, ParsePath, ParseHash, ParseSearch>;
  params?: {
    title?: string;
  };
}) {
  const location = useLocation();

  React.useEffect(() => {
    if (props.params?.title) {
      document.title = props.params?.title;
    }
  }, [props.params?.title]);

  const routeProps = React.useMemo(() => {
    const parsed = parseRoute(props.route, window.location.href);
    return {
      def: props.route,
      search: parsed.search,
      path: parsed.path,
      hash: parsed.hash,
    } as RouteProps<Name, Path, ParsePath, ParseHash, ParseSearch>;
  }, [
    // This is needed because window.location.href is not reactive
    location,
  ]);

  return (
    <>
      <props.Component
        loc={routeProps}
      />
    </>
  );
}
